import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useSections = () => {
    const [sections, setSections] = useState([]);
    const [isUpdatedSection, setIsUpdatedSection] = useState(false);
    const [HideSectionsSkeleton, setHideSectionsSkeleton] = useState(false);
    const [sectionsStoreUsername, setSectionsStoreUsername] = useState('');
    const { sendRequest, error, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedSection: false,
        sectionsStoreUsername: '',
        sections: [],
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedSection,
        sectionsStoreUsername,
        sections,
    };

    const updateSections = (newSections) => {
        setSections(newSections);
        setIsUpdatedSection(true);
    };

    const getSectionByName = useCallback((propertyName, propertyValue) => {
        let foundObject
        if (sections) {
            foundObject = sections.find(
                (section) => section && section[propertyName] === propertyValue
            );
        }
        return foundObject || null;
    }, [sections]);

    const fetchSections = useCallback(async (storeUsername, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: storeUsername,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedSection && cache.sectionsStoreUsername === storeUsername
            ),
            getCachedValue: (cache) => cache.sections,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/sections/${storeUsername}`
                    );
                    updateSections(responseData);
                    setSectionsStoreUsername(storeUsername);
                    clearError();
                    return responseData;
                } catch (err) {
                    setIsUpdatedSection(true);
                    setSectionsStoreUsername(storeUsername);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const reloadSections = useCallback(
        (storeUsername) => fetchSections(storeUsername, { force: true }),
        [fetchSections]
    );

    const removeSections = useCallback(() => {
        pendingFetchesRef.current.clear();
        setSections([]);
        setSectionsStoreUsername('');
        setIsUpdatedSection(false);
        setHideSectionsSkeleton(false);
        clearError();
    }, [clearError]);

    useEffect(() => {
        removeSections();
    }, []);

    return {
        sections,
        updateSections,
        isUpdatedSection,
        removeSections,
        getSectionByName,
        fetchSections,
        reloadSections,
        HideSectionsSkeleton,
        setHideSectionsSkeleton,
        sectionError: error
    };
}

export default useSections;
